/**
 * 認証ポリシー詳細（閲覧専用）。
 * 評価フローは作成・編集の確認ステップと同じ `PolicyFlowPanel` で見せる。
 * 編集は `$id/edit` に分けてあり、閲覧だけならフォーム一式のチャンクを読み込まない。
 */
'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { Card } from '@/components/Card/card';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { FillScreen, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { toast } from '@/components/ui/use-toast';
import { PolicyFlowPanel, authPoliciesKeyPrefix, toPolicyFormValue, useAuthenticationPolicy, useAuthenticationPolicyPriority, useDeleteAuthenticationPolicy } from '@/features/authentication-policy';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { PolicyDetailDescription } from './-components/PolicyDetailDescription';
import { PolicyDetailHeader } from './-components/PolicyDetailHeader';

/** 詳細画面の主役なので、拡大なしでも読める高さを取る。 */
const FLOW_HEIGHT = 520;
export const Route = createFileRoute('/_staff/settings/authentication/_layout/policies/$id/')({
  component: AuthPolicyDetailPage
});
function AuthPolicyDetailPage() {
  const {
    id
  } = Route.useParams();
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: authPoliciesKeyPrefix
  });
  const navigate = useNavigate();
  const {
    data,
    isPending,
    isError
  } = useAuthenticationPolicy(id);
  const priority = useAuthenticationPolicyPriority(id);
  const deleteMutation = useDeleteAuthenticationPolicy();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  useDocumentTitle(data?.name ?? t($ => $.title));

  // 参照が変わるとフローのグラフを組み直すため、data 単位で固定する
  const form = useMemo(() => data == null ? null : toPolicyFormValue(data), [data]);
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        id
      });
      setIsDeleteOpen(false);
      toast({
        description: t($ => $.toast.deleted)
      });
      await navigate({
        to: '/settings/authentication/policies'
      });
    } catch (error) {
      toast({
        description: handleApiError(error, {
          language: i18n.language
        }).message,
        variant: 'destructive'
      });
    }
  };
  if (isPending) return <ViewportLoader />;
  if (isError || data == null || form == null) {
    return <FillScreen className="flex flex-col gap-4 px-12 py-5">
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </FillScreen>;
  }
  return <VStack className="gap-4">
      <PolicyDetailHeader policy={data} priority={priority} onDelete={() => setIsDeleteOpen(true)} />

      <VStack className="gap-6 px-12 pb-5">
        {data.description != null && data.description !== '' && <PolicyDetailDescription description={data.description} />}

        <Card className="p-6">
          <PolicyFlowPanel form={form} height={FLOW_HEIGHT} />
        </Card>
      </VStack>

      <ConfirmActionDialog isOpen={isDeleteOpen} isLoading={deleteMutation.isPending} title={t($ => $.delete.title)} description={data.attachmentCount > 0 ? t($ => $.delete.descriptionReferenced, {
      count: data.attachmentCount
    }) : t($ => $.delete.descriptionUnreferenced)} okLabel={t($ => $.delete.confirm)} okVariant="danger" onClose={() => setIsDeleteOpen(false)} onConfirm={() => {
      void handleDelete();
    }} />
    </VStack>;
}